import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Card,
  Table,
  Button,
  Tag,
  Input,
  Select,
  DatePicker,
  Modal,
  Form,
  Row,
  Col,
  Spin,
  Badge,
  Dropdown,
  Drawer,
  Tabs,
  Tooltip,
  Alert,
} from "antd";
import {
  PlusOutlined,
  MessageOutlined,
  PhoneOutlined,
  ReloadOutlined,
  RightOutlined,
  CloseOutlined,
  EyeOutlined,
  CalendarOutlined,
  DownloadOutlined,
  FileTextOutlined,
  StarOutlined,
  MoreOutlined,
  DeleteOutlined,
  EnvironmentOutlined,
  ExpandOutlined,
  CompressOutlined,
  CopyOutlined,
  FilterOutlined,
  ShareAltOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import dayjs from "dayjs";
import styled from "styled-components";
import FeedbackRequestForm from "../../features/feedback/components/FeedbackRequestForm.jsx";
import * as feedbackService from "../../features/feedback/services/feedbackService.js";
import * as orderService from "../../features/orders/services/orderService.js";
import TruncatedText from "../../shared/components/TruncatedText.jsx";
import { orderDownloadService } from "../../features/orders/services/downloadService.js";
import { useDeviceType } from "../../features/leads/hooks/useDeviceType.js";
import { logError, logInfo, logWarn } from "../../shared/utils/logger.js";
import { RouteMapWrapper } from "../../components/RouteMap";
import { fetchCustomerEmail } from "../../features/orders/services/customerEmailService.js";
import QuickOrderStatusUpdate from "../../features/orders/components/QuickOrderStatusUpdate.jsx";
import PartnerJobPackModal from "../../features/orders/components/PartnerJobPackModal.jsx";
import DeliveryHealthChip from "../../features/orders/components/DeliveryHealthChip.jsx";
import ProviderAvatar from "../../features/orders/components/ProviderAvatar.jsx";
import OperationalSummaryCards from "../../features/orders/components/OperationalSummaryCards.jsx";
import OrderJourneyPanel from "../../features/orders/components/OrderJourneyPanel.jsx";
import MobileDeliveryCard from "../../features/orders/components/MobileDeliveryCard.jsx";
import SwipeableDeliveryRow from "../../features/orders/components/SwipeableDeliveryRow.jsx";
import {
  computeOperationalSummary,
  computeOrderHealth,
  DEFAULT_HEALTH_THRESHOLDS,
  filterOrdersByTab,
} from "../../features/orders/services/orderOperationalStatus.js";
import { getMilestoneView } from "../../features/orders/services/orderMilestoneView.js";
import { getSetting } from "../../features/systemSettings/services/systemSettingsService.js";
import TripLabelChip, { CorridorTag } from "../../features/orders/components/TripLabelChip.jsx";
import GroupedOrdersSections from "../../features/orders/components/GroupedOrdersSections.jsx";
import {
  groupOrdersByCorridorAndTrip,
  resolveOrderCorridorWithTripsMap,
  formatCorridorGroupLabel,
} from "../../features/orders/utils/orderCorridorGrouping.js";
import { loadTripsMap } from "../../features/trips/services/tripService.js";

const { Search } = Input;
const { Option } = Select;

const GROUP_BY_LABELS = {
  corridor_trip: "Corridor & trip",
  corridor: "Corridor",
  customer: "Customer",
  date: "Date",
};

// Thin scrollbar styles
const thinScrollbarStyles = `
  /* Firefox */
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.2) transparent;

  /* Chrome, Safari, Edge */
  &::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.2);
    border-radius: 3px;
    transition: background-color 0.2s;
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: rgba(0, 0, 0, 0.3);
  }
`;

// New styled components for improved UX
const StickyCommandBar = styled.div`
  position: sticky;
  top: 0;
  z-index: 100;
  /* Match Layout chrome so sticky overlap isn't a second gray */
  background: #f9fafb;
  padding: 8px 0 16px;
  margin: 0 0 8px 0;
  border: none;
  box-shadow: none;

  @media (max-width: 768px) {
    padding: 4px 0 12px;
    margin: 0 0 8px 0;
  }
`;

const CommandBarContent = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
  padding: 12px 16px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const SearchContainer = styled.div`
  flex: 1;
  min-width: 200px;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const ViewSegmented = styled.div`
  display: flex;
  gap: 4px;
  background: #f3f4f6;
  padding: 4px;
  border-radius: 8px;

  @media (max-width: 768px) {
    width: 100%;
    overflow-x: auto;
    ${thinScrollbarStyles}
  }
`;

const ViewButton = styled(Button)`
  border: none;
  box-shadow: none;
  background: transparent;
  padding: 6px 16px;
  border-radius: 6px;
  transition: all 0.2s;

  ${(props) =>
    props.$active &&
    `
    background: white;
    color: #005249;
    font-weight: 600;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  `}

  &:hover {
    background: ${(props) => (props.$active ? "white" : "rgba(255, 255, 255, 0.5)")};
  }
`;

const MobileTabRow = styled.div`
  display: flex;
  gap: 4px;
  overflow-x: auto;
  margin-top: 10px;
  padding-bottom: 2px;
  -webkit-overflow-scrolling: touch;
  ${thinScrollbarStyles}
`;

const MobileTab = styled.button`
  flex: 0 0 auto;
  border: none;
  background: transparent;
  padding: 8px 10px;
  font-size: 13px;
  font-weight: ${(props) => (props.$active ? 700 : 500)};
  color: ${(props) => {
    if (props.$active) return "#005249";
    if (props.$danger) return "#ff4d4f";
    return "#6b7280";
  }};
  border-bottom: 3px solid
    ${(props) => (props.$active ? "#005249" : "transparent")};
  white-space: nowrap;
`;

const FilterButton = styled(Button)`
  position: relative;
`;

const FilterBadge = styled(Badge)`
  .ant-badge-count {
    background: #ff4d4f;
    font-size: 10px;
    min-width: 16px;
    height: 16px;
    line-height: 16px;
    padding: 0 4px;
  }
`;

const ActiveFiltersContainer = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
`;

const FilterChip = styled(Tag)`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    opacity: 0.8;
  }
`;

const QuickActionsContainer = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`;

const CompactStatsContainer = styled.div`
  display: flex;
  gap: 12px;
  padding: 12px 16px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  margin-bottom: 16px;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-start;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const CompactStatsItems = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: center;
  min-width: 0;
`;

const StatsCollapseButton = styled(Button)`
  margin-left: auto;
`;

const CompactStat = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 6px;
  transition: all 0.2s;

  ${(props) =>
    props.$active &&
    `
    background: #e6f7ff;
    border: 1px solid #1890ff;
  `}

  &:hover {
    background: #f5f5f5;
  }
`;

const StatLabel = styled.span`
  font-size: 12px;
  color: #666;
  font-weight: 500;
`;

const StatValue = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: ${(props) => props.$color || "#333"};
`;

const TabbedDetailPanel = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const TabContentWrapper = styled.div`
  flex: 1;
  overflow-y: auto;
  ${thinScrollbarStyles}
  
  .ant-tabs-content-holder {
    overflow-y: auto;
    ${thinScrollbarStyles}
  }
  
  .ant-tabs-tabpane {
    padding-right: 8px;
  }
`;

const QuickActionButton = styled(Button)`
  padding: 4px 8px;
  height: auto;
  font-size: 12px;
`;

const MobileBottomBar = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  border-top: 1px solid #e8e8e8;
  padding: 12px 16px;
  padding-bottom: calc(12px + env(safe-area-inset-bottom));
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  display: flex;
  gap: 8px;

  @media (min-width: 769px) {
    display: none;
  }
`;

const MobileContentWrapper = styled.div`
  padding-bottom: ${(props) => (props.$hasBottomBar ? "80px" : "0")};

  @media (min-width: 769px) {
    padding-bottom: 0;
  }
`;

const CollapsibleStats = styled.div`
  transition: max-height 0.3s ease;
  overflow: hidden;
  max-height: ${(props) => (props.$collapsed ? "0" : "200px")};
  margin-bottom: ${(props) => (props.$collapsed ? "0" : "16px")};
`;

// Styled Components
const StatCard = styled(Card)`
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: all 0.2s;
  position: relative;

  ${(props) =>
    props.$active &&
    `
    border: 2px solid #1890ff;
    box-shadow: 0 4px 12px rgba(24, 144, 255, 0.2);
  `}

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  .urgency-indicator {
    position: absolute;
    top: 8px;
    right: 8px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    animation: ${(props) => (props.$pulse ? "pulse 2s infinite" : "none")};
  }

  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
  min-width: 0; /* Prevent squishing */

  @media (max-width: 768px) {
    margin-bottom: 16px;
    flex-direction: column;
    gap: 12px;
  }
`;

const HeaderLeft = styled.div`
  flex: 1;
  min-width: 0; /* Prevent squishing in flex container */
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0; /* Prevent button compression */
`;

const SyncStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #999;
`;

const SectionTitle = styled.div`
  font-weight: 600;
  font-size: 12px;
  text-transform: uppercase;
  color: #666;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
`;

const WhatsAppButton = styled(Button)`
  margin-top: 8px;
  background: #25d366;
  border-color: #25d366;

  &:hover {
    background: #20ba5a;
    border-color: #20ba5a;
  }
`;

// New styled components for responsive redesign
const PageContainer = styled.div`
  padding: 24px;
  /* Transparent — Layout already provides #f9fafb. A second gray (#f5f5f5)
     here stacked two shades and showed through the sticky bar bleed. */
  background: transparent;
  ${thinScrollbarStyles}

  @media (max-width: 768px) {
    padding: 16px;
  }

  /* Apply thin scrollbar to Ant Design Table */
  .ant-table-body {
    ${thinScrollbarStyles}
  }

  .ant-table-container {
    ${thinScrollbarStyles}
  }

  .ant-drawer-body {
    ${thinScrollbarStyles}
  }

  /* Show quick actions on row hover */
  .ant-table-tbody > tr:hover .quick-actions {
    opacity: 1;
  }

  .ant-table-tbody > tr .quick-actions {
    opacity: 0;
    transition: opacity 0.2s;
  }

  /* Always show actions for selected row */
  .ant-table-tbody > tr.ant-table-row-selected .quick-actions {
    opacity: 1;
  }
`;

const MobileButtonContainer = styled.div`
  margin-bottom: 16px;
  min-width: 0; /* Prevent squishing */
`;

const PageTitle = styled.h1`
  color: #005249;
  margin: 0;
  font-size: 28px;
  font-weight: 600;
  min-width: 0; /* Prevent text squishing */
  word-wrap: break-word;
  overflow-wrap: break-word;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 20px;
    line-height: 1.2;
  }
`;

const PageSubtitle = styled.p`
  color: #666;
  margin: 8px 0 0 0;
  font-size: 16px;
  min-width: 0; /* Prevent text squishing */
  word-wrap: break-word;
  overflow-wrap: break-word;
  line-height: 1.4;

  @media (max-width: 768px) {
    font-size: 14px;
    line-height: 1.4;
  }
`;

const StatsRow = styled(Row)`
  margin-bottom: 24px;
`;

const PresetFilters = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 24px;
  flex-wrap: wrap;
`;

const PresetButton = styled(Button)`
  ${(props) =>
    props.$active &&
    `
    background: #005249;
    border-color: #005249;
    color: white;
    
    &:hover {
      background: #23897A;
      border-color: #23897A;
    }
  `}
`;

// Mobile Order Card
const MobileOrderCard = styled(Card)`
  margin-bottom: 12px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.2s;
  cursor: pointer;

  ${(props) => {
    if (props.$status === "pending") {
      return `border-left: 4px solid #faad14; background: #fff7e6;`;
    }
    if (props.$status === "in_progress") {
      return `border-left: 4px solid #1890ff; background: #e6f7ff;`;
    }
    if (props.$status === "completed") {
      return `border-left: 4px solid #52c41a; opacity: 0.7;`;
    }
    if (props.$status === "cancelled") {
      return `border-left: 4px solid #ff4d4f; opacity: 0.6;`;
    }
    return `border-left: 4px solid #d9d9d9;`;
  }}

  ${(props) =>
    props.$overdue &&
    `
    border-left: 4px solid #ff4d4f;
    background: #fff1f0;
  `}

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
`;

const OrderId = styled.div`
  font-weight: 600;
  font-size: 16px;
  color: #005249;
`;

const StatusBadge = styled(Tag)`
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 12px;
  margin: 0;
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CustomerInfo = styled.div`
  font-size: 14px;
  color: #333;
  font-weight: 500;
`;

const RouteInfo = styled.div`
  font-size: 13px;
  color: #666;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PriorityInfo = styled.div`
  font-size: 12px;
  color: #999;
`;

const CardActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
  flex-wrap: wrap;
`;

const ExpandedContent = styled.div`
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #e8e8e8;
`;

const ExpandedSection = styled.div`
  margin-bottom: 16px;
`;

const SectionContent = styled.div`
  font-size: 14px;
  color: #333;
`;

const WhatsAppButtonMobile = styled(Button)`
  background: #25d366;
  border-color: #25d366;
  color: white;

  &:hover {
    background: #20ba5a;
    border-color: #20ba5a;
    color: white;
  }
`;

// Tablet Split View
const TabletContainer = styled.div`
  display: flex;
  gap: 16px;
  height: calc(100vh - 300px);
  min-height: 600px;

  @media (max-width: 1024px) {
    height: calc(100vh - 350px);
  }
`;

const TabletListPanel = styled.div`
  flex: 0 0 400px;
  overflow-y: auto;
  padding-right: 8px;
  ${thinScrollbarStyles}

  @media (max-width: 1024px) {
    flex: 0 0 350px;
  }
`;

const TabletDetailPanel = styled.div`
  flex: 1;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 24px;
  overflow-y: auto;
  ${thinScrollbarStyles}
`;

const TabletOrderCard = styled(Card)`
  margin-bottom: 12px;
  cursor: pointer;
  transition: all 0.2s;

  ${(props) =>
    props.$selected &&
    `
    border: 2px solid #1890ff;
    box-shadow: 0 4px 12px rgba(24, 144, 255, 0.2);
  `}

  ${(props) => {
    if (props.$status === "pending") {
      return `border-left: 4px solid #faad14;`;
    }
    if (props.$status === "in_progress") {
      return `border-left: 4px solid #1890ff;`;
    }
    if (props.$status === "completed") {
      return `border-left: 4px solid #52c41a; opacity: 0.7;`;
    }
    return `border-left: 4px solid #d9d9d9;`;
  }}

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

// Desktop Split View — hug content when few rows; fill viewport when scrolling many
const DesktopContainer = styled.div`
  display: flex;
  gap: 16px;
  align-items: stretch;
  min-height: 280px;
  max-height: calc(100vh - 280px);
  min-width: 0;
  /* Contain flex children when viewport height is locked */
  overflow: ${(props) => (props.$fillViewport ? "hidden" : "visible")};
  height: ${(props) => (props.$fillViewport ? "calc(100vh - 280px)" : "auto")};
`;

const DesktopTableContainer = styled.div`
  flex: 1;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 16px;
  min-height: 0;
  min-width: 0;
  overflow-y: auto;
  overflow-x: hidden;
  ${thinScrollbarStyles}
`;

const DesktopDetailPanel = styled.div`
  flex: 0 0 400px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 24px;
  display: flex;
  flex-direction: column;
  min-height: 280px;
  max-height: calc(100vh - 280px);
  overflow-y: auto;
  ${thinScrollbarStyles}
`;

const DetailPanelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e8e8e8;
`;

const DetailPanelTitle = styled.h3`
  margin: 0;
  color: #005249;
  font-size: 18px;
  font-weight: 600;
`;

const DetailPanelContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const DetailSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const DetailLabel = styled.div`
  font-size: 12px;
  text-transform: uppercase;
  color: #666;
  letter-spacing: 0.5px;
  font-weight: 600;
`;

const DetailValue = styled.div`
  font-size: 14px;
  color: #333;
`;

const DetailActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: auto;
  padding-top: 24px;
  border-top: 1px solid #e8e8e8;
`;

// Grouping
const OrderGroup = styled.div`
  margin-bottom: 24px;
`;

const GroupHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #f5f5f5;
  border-radius: 8px;
  margin-bottom: 12px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #e8e8e8;
  }
`;

const GroupTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: #005249;
`;

const GroupCount = styled.div`
  font-size: 12px;
  color: #666;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const OrdersPage = () => {
  const navigate = useNavigate();
  const { isMobile, isTablet } = useDeviceType();

  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [form] = Form.useForm();

  // Filter and view state — operational tabs (Sprint 8.5)
  // active | needs_attention | upcoming | completed | cancelled | collecting_today | in_transit | delivering_today
  const [presetFilter, setPresetFilter] = useState("active");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [healthThresholds, setHealthThresholds] = useState(DEFAULT_HEALTH_THRESHOLDS);

  // Grouping state
  const [groupBy, setGroupBy] = useState("corridor_trip");
  const [tripAssignmentFilter, setTripAssignmentFilter] = useState("all");
  const [tripsMap, setTripsMap] = useState({});

  // Sync state
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isSyncing, setIsSyncing] = useState(false);

  // Feedback state
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [feedbackAlert, setFeedbackAlert] = useState(null);

  // Map drawer state
  const [mapDrawerVisible, setMapDrawerVisible] = useState(false);
  const [mapRoute, setMapRoute] = useState(null); // { pickup, delivery, pickupAddress, deliveryAddress }
  const [mapDrawerExpanded, setMapDrawerExpanded] = useState(false);

  // Partner job pack modal state
  const [partnerJobPackModalVisible, setPartnerJobPackModalVisible] = useState(false);

  // New UX state
  const [filterDrawerVisible, setFilterDrawerVisible] = useState(false);
  const [activeDetailTab, setActiveDetailTab] = useState("journey");
  const [dateRangeFilter, setDateRangeFilter] = useState(null);

  // Debug: Log drawer state changes
  useEffect(() => {
    console.log("Map drawer state:", { mapDrawerVisible, mapRoute });
  }, [mapDrawerVisible, mapRoute]);

  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    inProgress: 0,
    completed: 0,
    cancelled: 0,
  });

  // Operational summary cards (prefer server Phase 2 summary, fall back to client)
  const [serverSummary, setServerSummary] = useState(null);

  const operationalSummary = useMemo(() => {
    if (serverSummary?.summary) return serverSummary.summary;
    return computeOperationalSummary(orders, healthThresholds);
  }, [orders, healthThresholds, serverSummary]);

  const tabCounts = useMemo(() => {
    if (serverSummary?.tabCounts) return serverSummary.tabCounts;
    return {
      active: filterOrdersByTab(orders, "active", healthThresholds).length,
      needs_attention: filterOrdersByTab(orders, "needs_attention", healthThresholds)
        .length,
      upcoming: filterOrdersByTab(orders, "upcoming", healthThresholds).length,
      completed: filterOrdersByTab(orders, "completed", healthThresholds).length,
      cancelled: filterOrdersByTab(orders, "cancelled", healthThresholds).length,
    };
  }, [orders, healthThresholds, serverSummary]);

  // Calculate eligible orders count for partner job pack
  const eligibleOrdersCount = useMemo(() => {
    return orders.filter(
      (order) => order.status === "pending" || order.status === "in_progress"
    ).length;
  }, [orders]);

  // Load Phase-1 health thresholds from System Settings (private, staff auth)
  useEffect(() => {
    let cancelled = false;
    const loadThresholds = async () => {
      try {
        const [staleRes, windowRes] = await Promise.all([
          getSetting("ops.deliveries.staleUpdateHours").catch(() => null),
          getSetting("ops.deliveries.attentionWindowHours").catch(() => null),
        ]);
        if (cancelled) return;
        const stale =
          staleRes?.data?.value ??
          staleRes?.value ??
          DEFAULT_HEALTH_THRESHOLDS.staleUpdateHours;
        const windowHrs =
          windowRes?.data?.value ??
          windowRes?.value ??
          DEFAULT_HEALTH_THRESHOLDS.attentionWindowHours;
        setHealthThresholds({
          staleUpdateHours: Number(stale) || DEFAULT_HEALTH_THRESHOLDS.staleUpdateHours,
          attentionWindowHours:
            Number(windowHrs) || DEFAULT_HEALTH_THRESHOLDS.attentionWindowHours,
        });
      } catch (error) {
        logWarn("Using default delivery health thresholds", {
          error: error.message,
        });
      }
    };
    loadThresholds();
    return () => {
      cancelled = true;
    };
  }, []);

  // Helper functions
  const isOverdue = (order) => {
    // Use expected delivery date, not pickup date
    const expectedDelivery =
      order.estimatedDelivery || order.booking?.estimatedDelivery;
    if (!expectedDelivery) return false;

    const deliveryDate = new Date(expectedDelivery);
    // Set to end of day (23:59:59.999) so the full day must pass before overdue
    deliveryDate.setHours(23, 59, 59, 999);

    const now = new Date();

    return (
      deliveryDate < now &&
      order.status !== "completed" &&
      order.status !== "cancelled"
    );
  };

  const isPendingOld = (order) => {
    if (order.status !== "pending") return false;
    const createdAt = new Date(order.createdAt);
    const now = new Date();
    const hoursDiff = (now - createdAt) / (1000 * 60 * 60);
    return hoursDiff > 24;
  };

  const formatRelativeTime = (date) => {
    const seconds = Math.floor((new Date() - date) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ago`;
  };

  const formatPhoneForWhatsApp = (phone) => {
    if (!phone) return null;

    // Remove all non-digit characters
    const digitsOnly = phone.replace(/\D/g, "");

    // If it starts with 27 (South Africa country code), use as is
    // If it starts with 0, replace with 27
    // Otherwise, assume it's already in international format
    if (digitsOnly.startsWith("27")) {
      return digitsOnly;
    } else if (digitsOnly.startsWith("0")) {
      return "27" + digitsOnly.substring(1);
    } else if (digitsOnly.length >= 9) {
      // Assume it's a local number, add 27 prefix
      return "27" + digitsOnly;
    }

    return digitsOnly;
  };

  // Calculate active filters count
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (statusFilter !== "all") count++;
    if (priorityFilter !== "all") count++;
    if (groupBy && groupBy !== "corridor_trip") count++;
    if (tripAssignmentFilter !== "all") count++;
    if (dateRangeFilter) count++;
    return count;
  }, [statusFilter, priorityFilter, groupBy, tripAssignmentFilter, dateRangeFilter]);

  const applyFilters = useCallback(() => {
    let filtered = [...orders];

    // Operational tab / card filter — only if status filter is not set
    if (statusFilter === "all") {
      const tabKeys = [
        "active",
        "needs_attention",
        "upcoming",
        "completed",
        "cancelled",
      ];
      if (tabKeys.includes(presetFilter)) {
        filtered = filterOrdersByTab(filtered, presetFilter, healthThresholds);
      } else if (presetFilter === "collecting_today") {
        filtered = filterOrdersByTab(filtered, "active", healthThresholds).filter(
          (order) => {
            const moveDate = order.booking?.moveDate || order.moveDate;
            if (!moveDate || order.status !== "booked") return false;
            const today = new Date();
            const d = new Date(moveDate);
            return (
              d.getFullYear() === today.getFullYear() &&
              d.getMonth() === today.getMonth() &&
              d.getDate() === today.getDate()
            );
          }
        );
      } else if (presetFilter === "in_transit") {
        filtered = filterOrdersByTab(filtered, "active", healthThresholds).filter(
          (o) =>
            o.status === "in_progress" || o.status === "in_transit_from_storage"
        );
      } else if (presetFilter === "delivering_today") {
        filtered = filterOrdersByTab(filtered, "active", healthThresholds).filter(
          (order) => {
            const eta =
              order.estimatedDelivery || order.booking?.estimatedDelivery;
            if (!eta) return false;
            const today = new Date();
            const d = new Date(eta);
            const sameDay =
              d.getFullYear() === today.getFullYear() &&
              d.getMonth() === today.getMonth() &&
              d.getDate() === today.getDate();
            return (
              sameDay &&
              (order.status === "in_transit_from_storage" ||
                order.status === "arrived_at_storage" ||
                order.status === "in_progress")
            );
          }
        );
      }
    }

    // Apply status filter from dropdown (takes precedence over preset if set)
    if (statusFilter !== "all") {
      filtered = filtered.filter((o) => o.status === statusFilter);
    }

    // Apply search filter
    if (searchQuery) {
      filtered = filtered.filter(
        (order) =>
          order.id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          order.customer?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          order.customerPhone?.includes(searchQuery) ||
          (order.pickup?.address || order.pickup || "")
            .toString()
            .toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          (order.delivery?.address || order.delivery || "")
            .toString()
            .toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          resolveOrderCorridorWithTripsMap(order, tripsMap)
            .toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          (order.tripId || "")
            .toLowerCase()
            .includes(searchQuery.toLowerCase())
      );
    }

    if (tripAssignmentFilter === "assigned") {
      filtered = filtered.filter((order) => Boolean(order.tripId));
    } else if (tripAssignmentFilter === "unassigned") {
      filtered = filtered.filter((order) => !order.tripId);
    }

    // Apply priority filter
    if (priorityFilter !== "all") {
      if (priorityFilter === "urgent") {
        filtered = filtered.filter((o) => o.priority === "high");
      } else {
        filtered = filtered.filter(
          (o) => o.priority === "medium" || o.priority === "low"
        );
      }
    }

    // Apply date range filter
    if (dateRangeFilter && dateRangeFilter.length === 2) {
      filtered = filtered.filter((order) => {
        const moveDate = order.booking?.moveDate || order.moveDate;
        if (!moveDate) return false;
        const orderDate = new Date(moveDate);
        orderDate.setHours(0, 0, 0, 0);
        return (
          orderDate >= dateRangeFilter[0] &&
          orderDate <= dateRangeFilter[1]
        );
      });
    }

    setFilteredOrders(filtered);
  }, [
    orders,
    presetFilter,
    statusFilter,
    searchQuery,
    priorityFilter,
    dateRangeFilter,
    healthThresholds,
    tripAssignmentFilter,
    tripsMap,
  ]);

  useEffect(() => {
    loadOrders();
    loadStats();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  useEffect(() => {
    let cancelled = false;
    const tripIds = orders.map((order) => order.tripId).filter(Boolean);
    if (!tripIds.length) {
      setTripsMap({});
      return undefined;
    }

    loadTripsMap(tripIds).then((map) => {
      if (!cancelled) setTripsMap(map);
    });

    return () => {
      cancelled = true;
    };
  }, [orders]);

  const corridorTripGroups = useMemo(() => {
    if (groupBy !== "corridor_trip") return null;
    return groupOrdersByCorridorAndTrip(filteredOrders, { tripsMap });
  }, [filteredOrders, groupBy, tripsMap]);

  const loadOrders = async () => {
    setLoading(true);
    setIsSyncing(true);
    try {
      const [result, summaryResult] = await Promise.all([
        orderService.getOrders(),
        orderService.getOperationalSummary().catch((err) => {
          logError("Failed to load operational summary", {
            error: err?.message || String(err),
          });
          return { success: false };
        }),
      ]);
      if (result.success) {
        const ordersData = result.data || [];
        setOrders(ordersData);
        setFilteredOrders(ordersData);
        setLastUpdated(new Date());
        logInfo("Orders loaded successfully", { count: ordersData.length });
      } else {
        logError("Failed to load orders", { error: result.error });
        message.error(result.error || "Failed to load orders");
      }
      if (summaryResult?.success && summaryResult.data) {
        setServerSummary(summaryResult.data);
        if (summaryResult.data.thresholds) {
          setHealthThresholds((prev) => ({
            ...prev,
            ...summaryResult.data.thresholds,
          }));
        }
      }
    } catch (error) {
      logError("Failed to load orders", { error: error.message });
      message.error("Failed to load orders");
    } finally {
      setLoading(false);
      setIsSyncing(false);
    }
  };

  const loadStats = async () => {
    try {
      const result = await orderService.getOrderStats();
      if (result.success) {
        setStats(result.data);
        logInfo("Order stats loaded successfully");
      } else {
        logError("Failed to load stats", { error: result.error });
      }
    } catch (error) {
      logError("Error loading stats", { error: error.message });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "green";
      case "in_progress":
        return "blue";
      case "pending":
        return "orange";
      case "cancelled":
        return "red";
      default:
        return "default";
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "high":
        return "#ff4d4f";
      case "medium":
      case "low":
        return "#52c41a";
      default:
        return "default";
    }
  };

  const getDisplayPriority = (priority) => {
    if (priority === "high") return "urgent";
    return "normal";
  };

  // Format date for display
  const formatDateDisplay = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return dayjs(dateString).format("MMM DD, YYYY");
    } catch {
      return "N/A";
    }
  };

  // Get pickup date from order
  const getPickupDate = (order) => {
    return order.booking?.moveDate || order.moveDate || null;
  };

  // Get expected delivery date from order
  const getExpectedDeliveryDate = (order) => {
    return order.estimatedDelivery || order.booking?.estimatedDelivery || null;
  };

  // Handle route click - open map drawer
  const handleRouteClick = async (order, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    const pickup = order.pickup?.address || order.pickup;
    const delivery = order.delivery?.address || order.delivery;

    console.log("Route clicked:", { pickup, delivery, order });

    if (pickup && delivery) {
      // Fetch structured addresses if addressSetId is available
      let pickupAddress = null;
      let deliveryAddress = null;

      if (order.addressSetId) {
        try {
          const api = (await import("../../shared/api.jsx")).default;
          const response = await api.get(`/address-sets/${order.addressSetId}/addresses`);
          if (response.success && response.data) {
            pickupAddress = response.data.pickupAddress;
            deliveryAddress = response.data.deliveryAddress;
            logInfo("OrdersPage: Fetched structured addresses", {
              addressSetId: order.addressSetId,
              hasPickup: !!pickupAddress,
              hasDelivery: !!deliveryAddress,
            });
          }
        } catch (err) {
          logError("OrdersPage: Failed to fetch structured addresses", {
            addressSetId: order.addressSetId,
            error: err.message,
          });
          // Continue with string addresses as fallback
        }
      }

      // Also check if order already has structured addresses
      if (!pickupAddress && order.pickupAddress) {
        pickupAddress = order.pickupAddress;
      }
      if (!deliveryAddress && order.deliveryAddress) {
        deliveryAddress = order.deliveryAddress;
      }

      setMapRoute({
        pickup,
        delivery,
        pickupAddress,
        deliveryAddress,
      });
      setMapDrawerVisible(true);
      console.log("Opening map drawer");
    } else {
      console.warn("Missing addresses:", { pickup, delivery });
      message.warning("Pickup or delivery address is missing");
    }
  };

  // Generate Google Maps URL for route (for opening in new tab)
  const getGoogleMapsUrl = (pickup, delivery) => {
    if (!pickup || !delivery) return null;
    const encodedPickup = encodeURIComponent(pickup);
    const encodedDelivery = encodeURIComponent(delivery);
    return `https://www.google.com/maps/dir/?api=1&origin=${encodedPickup}&destination=${encodedDelivery}`;
  };

  // Status-driven actions helper
  const getStatusActions = (status) => {
    switch (status) {
      case "pending":
      case "booked":
        return [
          { key: "assign", label: "Assign Driver", primary: true },
          { key: "update", label: "Update Status" },
        ];
      case "in_progress":
        return [
          { key: "update", label: "Update Status", primary: true },
          { key: "assign", label: "Change Driver" },
        ];
      case "arrived_at_storage":
        return [
          { key: "update", label: "Schedule Delivery", primary: true },
          { key: "assign", label: "Change Driver" },
        ];
      case "in_transit_from_storage":
        return [
          { key: "update", label: "Mark Delivered", primary: true },
        ];
      case "completed":
        return [{ key: "close", label: "Close Order", primary: true }];
      case "cancelled":
        return [{ key: "archive", label: "Archive", primary: true }];
      default:
        return [{ key: "update", label: "Update Status", primary: true }];
    }
  };

  // Get all order menu items (downloads, booking, etc.)
  const getOrderMenuItems = (order) => {
    return [
      {
        key: "view",
        label: "View Details",
        icon: <EyeOutlined />,
        onClick: () =>
          navigate(`/orders/${order.id}`, {
            state: { order: order },
          }),
      },
      {
        key: "view-booking",
        label: "View Booking",
        icon: <CalendarOutlined />,
        onClick: () =>
          navigate(`/bookings/${order.id}`, {
            state: {
              source: "order",
              sourceId: order.id,
              sourcePath: "/orders",
            },
          }),
      },
      {
        type: "divider",
      },
      {
        key: "download-driver",
        label: "Driver Pickup PDF",
        icon: <DownloadOutlined />,
        onClick: () => handleDownloadDriverPdf(order.id),
      },
      {
        key: "download-client",
        label: "Client Pickup PDF",
        icon: <FileTextOutlined />,
        onClick: () => handleDownloadClientPdf(order.id),
      },
      {
        type: "divider",
      },
      {
        key: "feedback",
        label: "Request Feedback",
        icon: <StarOutlined />,
        onClick: () => handleRequestFeedback(order),
      },
      {
        type: "divider",
      },
      {
        key: "delete",
        label: "Delete",
        icon: <DeleteOutlined />,
        danger: true,
        onClick: () => handleDeleteOrder(order),
      },
    ];
  };

  const handleEditOrder = async (order) => {
    // If customerEmail is missing, fetch it with fallback to name/phone search
    if (!order.customerEmail && order.customerId) {
      try {
        const customerEmail = await fetchCustomerEmail(
          order.customerId,
          order.customer, // Pass customer name for fallback
          order.customerPhone // Pass customer phone for fallback
        );
        if (customerEmail) {
          order = { ...order, customerEmail };
        }
      } catch (err) {
        logWarn("Failed to fetch customer email in OrdersPage", {
          customerId: order.customerId,
          customerName: order.customer,
          customerPhone: order.customerPhone,
          error: err.message,
        });
      }
    }

    // Debug logging
    logInfo("OrdersPage: order loaded for editing", {
      orderId: order.id,
      hasCustomerEmail: !!order.customerEmail,
      customerEmail: order.customerEmail || "NOT FOUND",
      customerId: order.customerId,
    });

    setSelectedOrder(order);
    setIsEditMode(true);
    // Transform order data for form fields
    const formData = {
      ...order,
      pickup: order.pickup?.address || order.pickup,
      delivery: order.delivery?.address || order.delivery,
      estimatedDelivery: order.estimatedDelivery
        ? dayjs(order.estimatedDelivery)
        : null,
    };
    form.setFieldsValue(formData);
    setIsModalVisible(true);
  };

  const handleDeleteOrder = async (order) => {
    Modal.confirm({
      title: "Delete Order",
      content: (
        <div>
          <p>Are you sure you want to delete this order?</p>
          <div
            style={{
              marginTop: "8px",
              padding: "8px",
              backgroundColor: "#f5f5f5",
              borderRadius: "4px",
            }}
          >
            <p>
              <strong>Order ID:</strong> {order.id}
            </p>
            <p>
              <strong>Customer:</strong> {order.customer}
            </p>
            <p>
              <strong>Route:</strong> {order.pickup?.address || order.pickup} →{" "}
              {order.delivery?.address || order.delivery}
            </p>
            <p>
              <strong>Status:</strong> {order.status}
            </p>
          </div>
          <p style={{ marginTop: "8px", color: "#ff4d4f", fontSize: "12px" }}>
            ⚠️ This action cannot be undone. The order will be moved to the
            deleted items.
          </p>
        </div>
      ),
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          const result = await orderService.deleteOrder(order.id);
          if (result.success) {
            message.success("Order deleted successfully");
            await loadOrders(); // Reload the list
          } else {
            message.error(result.error || "Failed to delete order");
          }
        } catch (error) {
          logError("Failed to delete order", {
            error: error.message,
            orderId: order.id,
          });
          message.error("Failed to delete order");
        }
      },
    });
  };

  const handleCreateOrder = () => {
    form.resetFields();
    setSelectedOrder(null);
    setIsEditMode(true);
    setIsModalVisible(true);
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
    setSelectedOrder(null);
    setIsEditMode(false);
  };

  const handleSwitchToEdit = () => {
    setIsEditMode(true);
  };

  const handleRequestFeedback = (order) => {
    // Prepare pre-populated data for feedback form
    const prePopulatedData = {
      customerId: order.customer || order.id, // Use customer name or order ID as customer ID
      phone: order.customerPhone,
      customerName: order.customer,
      referenceId: order.id,
      referenceType: "order",
      serviceDetails: `Order ${order.id} - Moving from ${order.pickup?.address || order.pickup} to ${order.delivery?.address || order.delivery}`,
      adminNotes: `Feedback request for order ${order.id}`,
    };

    // Store the pre-populated data and show feedback form
    setSelectedOrder({ ...order, prePopulatedData });
    setShowFeedbackForm(true);
    setFeedbackAlert(null);
  };

  const handleDownloadDriverPdf = async (orderId) => {
    try {
      await orderDownloadService.downloadDriverPickupPdf(orderId);
      message.success("Driver pickup PDF downloaded successfully");
    } catch (err) {
      message.error(err.message || "Failed to download driver pickup PDF");
    }
  };

  const handleDownloadClientPdf = async (orderId) => {
    try {
      await orderDownloadService.downloadClientPickupPdf(orderId);
      message.success("Client pickup PDF downloaded successfully");
    } catch (err) {
      message.error(err.message || "Failed to download client pickup PDF");
    }
  };

  const handleFeedbackSubmit = async (formData) => {
    setFeedbackLoading(true);
    try {
      // Add adminId to the form data (using a default admin ID for now)
      const requestData = {
        ...formData,
        adminId: "admin_001", // TODO: Get from authentication context
      };

      const result = await feedbackService.createFeedbackRequest(requestData);
      if (result.success) {
        setFeedbackAlert({
          type: "success",
          message: "Feedback request created successfully!",
        });
        setShowFeedbackForm(false);
        setSelectedOrder(null);
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      setFeedbackAlert({
        type: "error",
        message: error.message || "Failed to create feedback request",
      });
    } finally {
      setFeedbackLoading(false);
    }
  };

  const handleFeedbackCancel = () => {
    setShowFeedbackForm(false);
    setSelectedOrder(null);
    setFeedbackAlert(null);
  };

  // Mobile handlers
  const handleMobileAction = (action, order) => {
    switch (action) {
      case "assign":
        // Real assign path: booking edit (driver/team Autocomplete)
        navigate(`/bookings/${order.id}`, {
          state: {
            source: "order",
            sourceId: order.id,
            sourcePath: "/orders",
            openEdit: true,
          },
        });
        break;
      case "update":
        navigate(`/orders/${order.id}`, { state: { order } });
        break;
      case "call":
        if (order.customerPhone) {
          window.open(`tel:${order.customerPhone}`);
        } else {
          message.warning("No customer phone on file");
        }
        break;
      case "close":
        navigate(`/orders/${order.id}`, { state: { order } });
        break;
      case "archive":
        handleDeleteOrder(order);
        break;
      default:
        break;
    }
  };

  // Group orders
  const groupedOrders = useMemo(() => {
    if (!groupBy) return { ungrouped: filteredOrders };

    const groups = {};
    filteredOrders.forEach((order) => {
      let key = "ungrouped";
      if (groupBy === "customer") {
        key = order.customer || "Unknown";
      } else if (groupBy === "date") {
        const moveDate = order.booking?.moveDate || order.moveDate;
        key = moveDate ? new Date(moveDate).toLocaleDateString() : "No Date";
      } else if (groupBy === "route" || groupBy === "corridor") {
        key = resolveOrderCorridorWithTripsMap(order, tripsMap);
      }

      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(order);
    });

    return groups;
  }, [filteredOrders, groupBy, tripsMap]);

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();

      // Prepare order data
      const orderData = {
        customer: values.customer,
        customerPhone: values.customerPhone,
        pickup: {
          address: values.pickup,
        },
        delivery: {
          address: values.delivery,
        },
        items: values.items || [{ description: "General items", quantity: 1 }],
        status: values.status || "pending",
        priority: values.priority || "medium",
        estimatedDelivery: values.estimatedDelivery
          ? values.estimatedDelivery.toISOString()
          : null,
      };

      if (selectedOrder) {
        // Preserve customerId on edits (never generate a new one here)
        if (selectedOrder.customerId) {
          orderData.customerId = selectedOrder.customerId;
        }

        // If status changed, update it via the dedicated status endpoint (triggers emails/notifications)
        const previousStatus = selectedOrder.status;
        const nextStatus = orderData.status;
        if (previousStatus !== nextStatus) {
          await orderService.updateOrderStatus(selectedOrder.id, nextStatus);
        }

        // Update remaining fields via PUT (exclude status so we don't bypass the status workflow)
        const { status, ...otherUpdates } = orderData;

        // Update existing order
        const result = await orderService.updateOrder(selectedOrder.id, otherUpdates);
        if (result.success) {
          message.success("Order updated successfully");
          await loadOrders(); // Reload the list
        } else {
          message.error(result.error || "Failed to update order");
          return;
        }
      } else {
        // Generate unique customer ID only for new orders
        orderData.customerId = `CUST_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

        // Create new order
        const result = await orderService.createOrder(orderData);
        if (result.success) {
          message.success("Order created successfully");
          await loadOrders(); // Reload the list
        } else {
          message.error(result.error || "Failed to create order");
          return;
        }
      }

      setIsModalVisible(false);
      form.resetFields();
      setSelectedOrder(null);
    } catch (error) {
      logError("Form validation failed", { error: error.message });
    }
  };

  // Desktop table columns — milestone-first ops view (Sprint 8.5)
  const desktopColumns = [
    {
      title: "Order / Customer",
      key: "orderCustomer",
      width: 200,
      fixed: "left",
      render: (_, record) => (
        <div style={{ padding: "4px 0" }}>
          <div style={{ fontWeight: 600, fontSize: 13, wordBreak: "break-all" }}>
            {record.id}
          </div>
          <div style={{ fontSize: 12, color: "#595959" }}>
            <TruncatedText text={record.customer} maxLength={28} />
          </div>
          {record.customerPhone && (
            <div style={{ fontSize: 11, color: "#8c8c8c" }}>
              {record.customerPhone}
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Corridor",
      key: "corridor",
      width: 110,
      render: (_, record) => (
        <CorridorTag
          corridor={formatCorridorGroupLabel(
            resolveOrderCorridorWithTripsMap(record, tripsMap)
          )}
        />
      ),
    },
    {
      title: "Trip",
      key: "trip",
      width: 180,
      render: (_, record) => (
        <div onClick={(e) => e.stopPropagation()}>
          <TripLabelChip
            tripId={record.tripId}
            trip={record.tripId ? tripsMap[record.tripId] : null}
          />
        </div>
      ),
    },
    {
      title: "Route",
      key: "route",
      width: 200,
      render: (_, record) => (
        <div style={{ fontSize: 13 }}>
          <TruncatedText
            text={record.pickup?.address || record.pickup}
            maxLength={28}
          />
          <div style={{ color: "#8c8c8c", fontSize: 11, margin: "2px 0" }}>→</div>
          <TruncatedText
            text={record.delivery?.address || record.delivery}
            maxLength={28}
          />
        </div>
      ),
    },
    {
      title: "Current milestone",
      key: "currentMilestone",
      width: 160,
      render: (_, record) => {
        const { current } = getMilestoneView(record);
        const isTerminal =
          record.status === "completed" || record.status === "cancelled";
        const dotColor = isTerminal
          ? record.status === "cancelled"
            ? "#ff4d4f"
            : "#52c41a"
          : "#1890ff";
        return (
          <span style={{ fontSize: 13, lineHeight: 1.4 }}>
            <span style={{ color: dotColor, marginRight: 6 }}>
              {isTerminal && record.status === "completed" ? "✓" : "●"}
            </span>
            {current?.title || record.status?.replace(/_/g, " ")}
          </span>
        );
      },
    },
    {
      title: "Next / ETA",
      key: "nextEta",
      width: 180,
      render: (_, record) => {
        if (record.status === "completed") {
          return (
            <div style={{ fontSize: 13, color: "#8c8c8c" }}>
              <div>Complete</div>
            </div>
          );
        }
        if (record.status === "cancelled") {
          return (
            <div style={{ fontSize: 13, color: "#8c8c8c" }}>
              <div>Cancelled</div>
            </div>
          );
        }
        const { next } = getMilestoneView(record);
        const dueDate = getExpectedDeliveryDate(record);
        return (
          <div style={{ fontSize: 13, lineHeight: 1.4 }}>
            <div>{next?.title || "—"}</div>
            <div style={{ fontSize: 11, color: "#8c8c8c" }}>
              {dueDate ? `ETA ${formatDateDisplay(dueDate)}` : "No ETA"}
            </div>
          </div>
        );
      },
    },
    {
      title: "Provider",
      key: "provider",
      width: 170,
      render: (_, record) => (
        <ProviderAvatar
          name={
            record.booking?.assignedTeam?.partnerName ||
            record.assignedTeam?.partnerName ||
            record.assignedTeam?.teamName ||
            record.booking?.assignedTeam?.teamName
          }
        />
      ),
    },
    {
      title: "Health",
      key: "health",
      width: 130,
      render: (_, record) => {
        const result =
          record?.loadHealth && typeof record.loadHealth === "object"
            ? record.loadHealth
            : computeOrderHealth(record, healthThresholds);
        return (
          <DeliveryHealthChip
            health={result.health}
            reason={result.reason}
            hoursSinceUpdate={result.hoursSinceUpdate}
            staleUpdateHours={healthThresholds.staleUpdateHours}
            applicable={result.applicable}
          />
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      fixed: "right",
      render: (_, record) => (
        <QuickActionsContainer
          onClick={(e) => e.stopPropagation()}
          className="quick-actions"
        >
          {record.customerPhone && (
            <>
              <Tooltip title="Call">
                <QuickActionButton
                  type="text"
                  size="small"
                  icon={<PhoneOutlined />}
                  href={`tel:${record.customerPhone}`}
                />
              </Tooltip>
              <Tooltip title="WhatsApp">
                <QuickActionButton
                  type="text"
                  size="small"
                  icon={<MessageOutlined />}
                  href={`https://wa.me/${formatPhoneForWhatsApp(record.customerPhone)}`}
                  target="_blank"
                  disabled={!formatPhoneForWhatsApp(record.customerPhone)}
                  style={{ color: "#25d366" }}
                />
              </Tooltip>
            </>
          )}
          <Tooltip title="View Map">
            <QuickActionButton
              type="text"
              size="small"
              icon={<EnvironmentOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                handleRouteClick(record, e);
              }}
            />
          </Tooltip>
          <Tooltip title="View Details">
            <QuickActionButton
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/orders/${record.id}`, { state: { order: record } });
              }}
            />
          </Tooltip>
          <Dropdown
            menu={{ items: getOrderMenuItems(record) }}
            trigger={["click"]}
          >
            <Tooltip title="More Actions">
              <QuickActionButton
                type="text"
                size="small"
                icon={<MoreOutlined />}
                onClick={(e) => e.stopPropagation()}
              />
            </Tooltip>
          </Dropdown>
        </QuickActionsContainer>
      ),
    },
  ];

  // Render mobile view — action-first delivery cards
  const renderMobileOrder = (order) => (
    <SwipeableDeliveryRow
      key={order.id}
      leftActions={[
        {
          key: "call",
          label: "Call",
          bg: "#1890ff",
          onClick: () => handleMobileAction("call", order),
        },
        {
          key: "update",
          label: "Update",
          bg: "#005249",
          onClick: () => handleMobileAction("update", order),
        },
      ]}
      rightActions={[
        {
          key: "assign",
          label: "Assign",
          bg: "#fa8c16",
          onClick: () => handleMobileAction("assign", order),
        },
        {
          key: "view",
          label: "Open",
          bg: "#52c41a",
          onClick: () =>
            navigate(`/orders/${order.id}`, { state: { order } }),
        },
      ]}
    >
      <MobileDeliveryCard
        order={order}
        trip={order.tripId ? tripsMap[order.tripId] : null}
        thresholds={healthThresholds}
        formatDateDisplay={formatDateDisplay}
        getExpectedDeliveryDate={getExpectedDeliveryDate}
        onPrimaryAction={handleMobileAction}
        onOpenMap={(o) => handleRouteClick(o)}
        onViewDetails={(o) =>
          navigate(`/orders/${o.id}`, { state: { order: o } })
        }
        sheetItems={getOrderMenuItems(order)}
      />
    </SwipeableDeliveryRow>
  );

  const renderMobileView = () => {
    if (groupBy === "corridor_trip") {
      return (
        <GroupedOrdersSections
          corridorGroups={corridorTripGroups}
          variant="mobile"
          renderMobileOrder={renderMobileOrder}
        />
      );
    }

    const ordersToRender = groupBy
      ? Object.values(groupedOrders).flat()
      : filteredOrders;

    if (ordersToRender.length === 0) {
      return (
        <div
          style={{
            textAlign: "center",
            padding: "48px 16px",
            color: "#9ca3af",
          }}
        >
          No deliveries in this view
        </div>
      );
    }

    return <div>{ordersToRender.map((order) => renderMobileOrder(order))}</div>;
  };

  // Render tablet view
  const renderTabletOrder = (order) => (
    <TabletOrderCard
      key={order.id}
      $selected={selectedOrder?.id === order.id}
      $status={order.status}
      onClick={() => setSelectedOrder(order)}
    >
      <div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 8,
            marginBottom: 8,
          }}
        >
          <div style={{ fontWeight: 600 }}>{order.id}</div>
          <div onClick={(e) => e.stopPropagation()}>
            <TripLabelChip
              tripId={order.tripId}
              trip={order.tripId ? tripsMap[order.tripId] : null}
            />
          </div>
        </div>
        <div style={{ fontSize: "14px", color: "#666" }}>{order.customer}</div>
        <div style={{ marginTop: 6 }}>
          <CorridorTag
            corridor={formatCorridorGroupLabel(
              resolveOrderCorridorWithTripsMap(order, tripsMap)
            )}
          />
        </div>
        <div
          style={{ fontSize: "12px", color: "#999", marginTop: "4px" }}
        >
          {order.pickup?.address || order.pickup} →{" "}
          {order.delivery?.address || order.delivery}
        </div>
        <div style={{ marginTop: "8px" }}>
          <Tag color={getStatusColor(order.status)}>
            {order.status.replace("_", " ").toUpperCase()}
          </Tag>
        </div>
      </div>
    </TabletOrderCard>
  );

  const renderTabletView = () => {
    const listContent =
      groupBy === "corridor_trip" ? (
        <GroupedOrdersSections
          corridorGroups={corridorTripGroups}
          variant="tablet"
          renderTabletOrder={renderTabletOrder}
        />
      ) : (
        filteredOrders.map((order) => renderTabletOrder(order))
      );

    return (
      <TabletContainer>
        <TabletListPanel>{listContent}</TabletListPanel>
        <TabletDetailPanel>
          {selectedOrder ? (
            <div>
              <DetailPanelHeader>
                <DetailPanelTitle>{selectedOrder.id}</DetailPanelTitle>
                <Button
                  icon={<CloseOutlined />}
                  type="text"
                  onClick={() => setSelectedOrder(null)}
                />
              </DetailPanelHeader>
              <DetailPanelContent>
                <DetailSection>
                  <DetailLabel>
                    Customer
                    <Button
                      type="link"
                      size="small"
                      icon={<CopyOutlined />}
                      onClick={async () => {
                        const { formatCustomerDetails } = await import("../../shared/utils/customerDetailsFormatter");
                        const { copyToClipboard } = await import("../../features/leads/services/communicationTemplateService");
                        const details = formatCustomerDetails(selectedOrder);
                        const success = await copyToClipboard(details);
                        if (success) {
                          message.success("Customer details copied to clipboard!");
                        } else {
                          message.error("Failed to copy customer details");
                        }
                      }}
                      style={{ marginLeft: 8, padding: 0, height: "auto" }}
                    >
                      Copy
                    </Button>
                  </DetailLabel>
                  <DetailValue>{selectedOrder.customer}</DetailValue>
                  {selectedOrder.customerPhone && (
                    <>
                      <DetailValue>
                        <a href={`tel:${selectedOrder.customerPhone}`}>
                          <PhoneOutlined /> {selectedOrder.customerPhone}
                        </a>
                      </DetailValue>
                      <WhatsAppButton
                        href={`https://wa.me/${formatPhoneForWhatsApp(selectedOrder.customerPhone)}`}
                        target="_blank"
                        type="primary"
                        icon={<MessageOutlined />}
                        disabled={
                          !formatPhoneForWhatsApp(selectedOrder.customerPhone)
                        }
                      >
                        WhatsApp
                      </WhatsAppButton>
                    </>
                  )}
                </DetailSection>
                <DetailSection>
                  <DetailLabel>Route</DetailLabel>
                  <DetailValue>
                    <div style={{ marginBottom: "12px" }}>
                      <div style={{ marginBottom: "8px" }}>
                        <strong>Pickup Date:</strong>{" "}
                        {formatDateDisplay(getPickupDate(selectedOrder))}
                      </div>
                      <div style={{ marginBottom: "8px" }}>
                        <strong>Expected Delivery:</strong>{" "}
                        {formatDateDisplay(
                          getExpectedDeliveryDate(selectedOrder)
                        )}
                      </div>
                    </div>
                    <div
                      style={{
                        padding: "12px",
                        backgroundColor: "#f5f5f5",
                        borderRadius: "8px",
                        cursor: "pointer",
                        transition: "all 0.2s",
                        border: "1px solid #e8e8e8",
                        position: "relative",
                        zIndex: 1,
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleRouteClick(selectedOrder, e);
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#e6f7ff";
                        e.currentTarget.style.borderColor = "#1890ff";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "#f5f5f5";
                        e.currentTarget.style.borderColor = "#e8e8e8";
                      }}
                    >
                      <div style={{ marginBottom: "8px" }}>
                        <EnvironmentOutlined
                          style={{ marginRight: "8px", color: "#1890ff" }}
                        />
                        <strong>Pickup:</strong>{" "}
                        {selectedOrder.pickup?.address || selectedOrder.pickup}
                      </div>
                      <div style={{ marginTop: "8px" }}>
                        <EnvironmentOutlined
                          style={{ marginRight: "8px", color: "#52c41a" }}
                        />
                        <strong>Delivery:</strong>{" "}
                        {selectedOrder.delivery?.address ||
                          selectedOrder.delivery}
                      </div>
                      <div
                        style={{
                          marginTop: "8px",
                          fontSize: "12px",
                          color: "#1890ff",
                        }}
                      >
                        Click to view on map →
                      </div>
                    </div>
                  </DetailValue>
                </DetailSection>
                <DetailSection>
                  <DetailLabel>Status</DetailLabel>
                  <DetailValue>
                    <QuickOrderStatusUpdate
                      order={selectedOrder}
                      onStatusUpdate={async (updatedOrder) => {
                        setSelectedOrder(updatedOrder);
                        // Update local state
                        setOrders((prevOrders) =>
                          prevOrders.map((o) =>
                            o.id === updatedOrder.id ? updatedOrder : o
                          )
                        );
                        setFilteredOrders((prevOrders) =>
                          prevOrders.map((o) =>
                            o.id === updatedOrder.id ? updatedOrder : o
                          )
                        );
                        // Reload orders in background
                        await loadOrders();
                      }}
                      showLabel={true}
                      size="middle"
                    />
                  </DetailValue>
                </DetailSection>
                <DetailActions>
                  {getStatusActions(selectedOrder.status).map((action) => (
                    <Button
                      key={action.key}
                      type={action.primary ? "primary" : "default"}
                      onClick={() =>
                        handleMobileAction(action.key, selectedOrder)
                      }
                      block
                    >
                      {action.label}
                    </Button>
                  ))}
                  <Dropdown
                    menu={{ items: getOrderMenuItems(selectedOrder) }}
                    trigger={["click"]}
                  >
                    <Button block>
                      <MoreOutlined /> More Actions
                    </Button>
                  </Dropdown>
                </DetailActions>
              </DetailPanelContent>
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                color: "#999",
              }}
            >
              Select an order to view details
            </div>
          )}
        </TabletDetailPanel>
      </TabletContainer>
    );
  };

  // Render desktop view
  const renderDesktopView = () => {
    // Ant Design reserves scroll.y as fixed body height — only enable when
    // there are enough rows, otherwise one row leaves a huge empty body +
    // a horizontal scrollbar stranded under the first row.
    const needsVerticalScroll = filteredOrders.length > 8;
    const isGroupedView = groupBy === "corridor_trip";
    const tableScroll = needsVerticalScroll
      ? { x: 1500, y: "calc(100vh - 480px)" }
      : { x: 1500 };
    // Stacked corridor/trip tables scroll via the container — not per-table body.
    const groupedTableScroll = { x: 1500 };
    const fillViewport =
      needsVerticalScroll ||
      Boolean(selectedOrder) ||
      (isGroupedView && filteredOrders.length > 0);

    return (
      <DesktopContainer $fillViewport={fillViewport}>
        <DesktopTableContainer>
          {groupBy === "corridor_trip" ? (
            <GroupedOrdersSections
              corridorGroups={corridorTripGroups}
              variant="desktop"
              desktopColumns={desktopColumns}
              loading={loading}
              selectedOrderId={selectedOrder?.id}
              onSelectOrder={(record) => {
                setSelectedOrder(record);
                setActiveDetailTab("journey");
              }}
              tableScroll={groupedTableScroll}
            />
          ) : (
            <Table
              columns={desktopColumns}
              dataSource={filteredOrders}
              loading={loading}
              rowKey="id"
              sticky={needsVerticalScroll}
              scroll={tableScroll}
              onRow={(record) => ({
                onClick: () => {
                  setSelectedOrder(record);
                  setActiveDetailTab("journey");
                },
                className:
                  selectedOrder?.id === record.id ? "ant-table-row-selected" : "",
                style: {
                  cursor: "pointer",
                  backgroundColor:
                    selectedOrder?.id === record.id ? "#e6f7ff" : "transparent",
                },
              })}
              pagination={{
                total: filteredOrders.length,
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total, range) =>
                  `Showing ${range[0]} to ${range[1]} of ${total} deliveries`,
              }}
              size="middle"
            />
          )}
        </DesktopTableContainer>
        <DesktopDetailPanel>
          {selectedOrder ? (
            <TabbedDetailPanel>
              <DetailPanelHeader>
                <DetailPanelTitle>{selectedOrder.id}</DetailPanelTitle>
                <Button
                  icon={<CloseOutlined />}
                  type="text"
                  onClick={() => setSelectedOrder(null)}
                />
              </DetailPanelHeader>
              
              <TabContentWrapper>
                <Tabs
                  activeKey={activeDetailTab}
                  onChange={setActiveDetailTab}
                  items={[
                    {
                      key: "journey",
                      label: "Journey",
                      children: (
                        <OrderJourneyPanel
                          order={selectedOrder}
                          onUpdateMilestone={() => {
                            setActiveDetailTab("summary");
                            message.info(
                              "Use the status control on Summary to update the milestone"
                            );
                          }}
                          onContactProvider={() => {
                            const phone =
                              selectedOrder?.booking?.assignedTeam?.primaryDriver
                                ?.phone ||
                              selectedOrder?.booking?.assignedTeam?.phone;
                            if (phone) {
                              window.open(`tel:${phone}`);
                            } else {
                              message.warning("No provider phone on file");
                            }
                          }}
                        />
                      ),
                    },
                    {
                      key: "summary",
                      label: "Summary",
                      children: (
                        <>
                          <DetailSection>
                            <DetailLabel>Status</DetailLabel>
                            <DetailValue>
                              <QuickOrderStatusUpdate
                                order={selectedOrder}
                                onStatusUpdate={async (updatedOrder) => {
                                  setSelectedOrder(updatedOrder);
                                  // Update local state
                                  setOrders((prevOrders) =>
                                    prevOrders.map((o) =>
                                      o.id === updatedOrder.id ? updatedOrder : o
                                    )
                                  );
                                  setFilteredOrders((prevOrders) =>
                                    prevOrders.map((o) =>
                                      o.id === updatedOrder.id ? updatedOrder : o
                                    )
                                  );
                                  // Reload orders in background
                                  await loadOrders();
                                }}
                                showLabel={true}
                                size="middle"
                              />
                              {isOverdue(selectedOrder) && (
                                <Tag color="red" style={{ marginTop: "8px", display: "block" }}>
                                  OVERDUE
                                </Tag>
                              )}
                            </DetailValue>
                          </DetailSection>
                          <DetailSection>
                            <DetailLabel>Health</DetailLabel>
                            <DetailValue>
                              {(() => {
                                const result =
                                  selectedOrder?.loadHealth &&
                                  typeof selectedOrder.loadHealth === "object"
                                    ? selectedOrder.loadHealth
                                    : computeOrderHealth(
                                        selectedOrder,
                                        healthThresholds
                                      );
                                return (
                                  <DeliveryHealthChip
                                    health={result.health}
                                    reason={result.reason}
                                    hoursSinceUpdate={result.hoursSinceUpdate}
                                    staleUpdateHours={
                                      healthThresholds.staleUpdateHours
                                    }
                                    applicable={result.applicable}
                                  />
                                );
                              })()}
                            </DetailValue>
                          </DetailSection>
                          <DetailSection>
                            <DetailLabel>Priority</DetailLabel>
                            <DetailValue>
                              {getDisplayPriority(selectedOrder.priority).toUpperCase()}
                            </DetailValue>
                          </DetailSection>
                          <DetailSection>
                            <DetailLabel>Created</DetailLabel>
                            <DetailValue>
                              {selectedOrder.createdAt
                                ? new Date(selectedOrder.createdAt).toLocaleDateString()
                                : "N/A"}
                            </DetailValue>
                          </DetailSection>
                          <DetailSection>
                            <DetailLabel>Expected Delivery</DetailLabel>
                            <DetailValue>
                              {formatDateDisplay(getExpectedDeliveryDate(selectedOrder))}
                            </DetailValue>
                          </DetailSection>
                        </>
                      ),
                    },
                    {
                      key: "customer",
                      label: "Customer",
                      children: (
                        <DetailSection>
                          <DetailLabel>
                            Customer
                            <Button
                              type="link"
                              size="small"
                              icon={<CopyOutlined />}
                              onClick={async () => {
                                const { formatCustomerDetails } = await import("../../shared/utils/customerDetailsFormatter");
                                const { copyToClipboard } = await import("../../features/leads/services/communicationTemplateService");
                                const details = formatCustomerDetails(selectedOrder);
                                const success = await copyToClipboard(details);
                                if (success) {
                                  message.success("Customer details copied to clipboard!");
                                } else {
                                  message.error("Failed to copy customer details");
                                }
                              }}
                              style={{ marginLeft: 8, padding: 0, height: "auto" }}
                            >
                              Copy
                            </Button>
                          </DetailLabel>
                          <DetailValue>{selectedOrder.customer}</DetailValue>
                          {selectedOrder.customerPhone && (
                            <>
                              <DetailValue>
                                <a href={`tel:${selectedOrder.customerPhone}`}>
                                  <PhoneOutlined /> {selectedOrder.customerPhone}
                                </a>
                              </DetailValue>
                              <WhatsAppButton
                                href={`https://wa.me/${formatPhoneForWhatsApp(selectedOrder.customerPhone)}`}
                                target="_blank"
                                type="primary"
                                icon={<MessageOutlined />}
                                disabled={
                                  !formatPhoneForWhatsApp(selectedOrder.customerPhone)
                                }
                              >
                                WhatsApp
                              </WhatsAppButton>
                            </>
                          )}
                        </DetailSection>
                      ),
                    },
                    {
                      key: "route",
                      label: "Route",
                      children: (
                        <DetailSection>
                          <DetailLabel>Route</DetailLabel>
                          <DetailValue>
                            <div style={{ marginBottom: "12px" }}>
                              <div style={{ marginBottom: "8px" }}>
                                <strong>Pickup Date:</strong>{" "}
                                {formatDateDisplay(getPickupDate(selectedOrder))}
                              </div>
                              <div style={{ marginBottom: "8px" }}>
                                <strong>Expected Delivery:</strong>{" "}
                                {formatDateDisplay(
                                  getExpectedDeliveryDate(selectedOrder)
                                )}
                              </div>
                            </div>
                            <div
                              style={{
                                padding: "12px",
                                backgroundColor: "#f5f5f5",
                                borderRadius: "8px",
                                cursor: "pointer",
                                transition: "all 0.2s",
                                border: "1px solid #e8e8e8",
                                position: "relative",
                                zIndex: 1,
                              }}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleRouteClick(selectedOrder, e);
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = "#e6f7ff";
                                e.currentTarget.style.borderColor = "#1890ff";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = "#f5f5f5";
                                e.currentTarget.style.borderColor = "#e8e8e8";
                              }}
                            >
                              <div style={{ marginBottom: "8px" }}>
                                <EnvironmentOutlined
                                  style={{ marginRight: "8px", color: "#1890ff" }}
                                />
                                <strong>Pickup:</strong>{" "}
                                {selectedOrder.pickup?.address || selectedOrder.pickup}
                              </div>
                              <div style={{ marginTop: "8px" }}>
                                <EnvironmentOutlined
                                  style={{ marginRight: "8px", color: "#52c41a" }}
                                />
                                <strong>Delivery:</strong>{" "}
                                {selectedOrder.delivery?.address ||
                                  selectedOrder.delivery}
                              </div>
                              <div
                                style={{
                                  marginTop: "8px",
                                  fontSize: "12px",
                                  color: "#1890ff",
                                }}
                              >
                                Click to view on map →
                              </div>
                            </div>
                          </DetailValue>
                        </DetailSection>
                      ),
                    },
                    {
                      key: "actions",
                      label: "Actions",
                      children: (
                        <DetailActions>
                          {getStatusActions(selectedOrder.status).map((action) => (
                            <Button
                              key={action.key}
                              type={action.primary ? "primary" : "default"}
                              onClick={() =>
                                handleMobileAction(action.key, selectedOrder)
                              }
                              block
                            >
                              {action.label}
                            </Button>
                          ))}
                          <Dropdown
                            menu={{ items: getOrderMenuItems(selectedOrder) }}
                            trigger={["click"]}
                          >
                            <Button block>
                              <MoreOutlined /> More Actions
                            </Button>
                          </Dropdown>
                        </DetailActions>
                      ),
                    },
                  ]}
                />
              </TabContentWrapper>
            </TabbedDetailPanel>
          ) : (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 200,
                flex: 1,
                color: "#999",
              }}
            >
              Select an order to view details
            </div>
          )}
        </DesktopDetailPanel>
      </DesktopContainer>
    );
  };

  // Get active filters for display
  const getActiveFilters = () => {
    const filters = [];
    if (statusFilter !== "all") {
      filters.push({ key: "status", label: `Status: ${statusFilter}`, onRemove: () => setStatusFilter("all") });
    }
    if (priorityFilter !== "all") {
      filters.push({ key: "priority", label: `Priority: ${priorityFilter}`, onRemove: () => setPriorityFilter("all") });
    }
    if (groupBy && groupBy !== "corridor_trip") {
      filters.push({
        key: "groupBy",
        label: `Group: ${GROUP_BY_LABELS[groupBy] || groupBy}`,
        onRemove: () => setGroupBy("corridor_trip"),
      });
    }
    if (tripAssignmentFilter !== "all") {
      filters.push({
        key: "tripAssignment",
        label:
          tripAssignmentFilter === "assigned"
            ? "Trip: Assigned"
            : "Trip: Unassigned",
        onRemove: () => setTripAssignmentFilter("all"),
      });
    }
    if (dateRangeFilter) {
      filters.push({ key: "dateRange", label: "Date Range", onRemove: () => setDateRangeFilter(null) });
    }
    return filters;
  };

  const clearAllFilters = () => {
    setStatusFilter("all");
    setPriorityFilter("all");
    setGroupBy("corridor_trip");
    setTripAssignmentFilter("all");
    setDateRangeFilter(null);
    setPresetFilter("active");
  };

  const handleSummaryCardSelect = (key) => {
    setStatusFilter("all");
    setPresetFilter(key);
  };

  return (
    <PageContainer>
      {/* Page Header */}
      <PageHeader>
        <HeaderLeft>
          <PageTitle>Active Deliveries</PageTitle>
          <PageSubtitle>
            Monitor live loads and operational exceptions
          </PageSubtitle>
        </HeaderLeft>
        <HeaderRight>
          {isSyncing && <Spin size="small" />}
          {lastUpdated && (
            <SyncStatus>
              <span>Last updated: {formatRelativeTime(lastUpdated)}</span>
            </SyncStatus>
          )}
          <Button
            icon={<ReloadOutlined />}
            onClick={loadOrders}
            loading={isSyncing}
          >
            Refresh
          </Button>
        </HeaderRight>
      </PageHeader>

      {/* Operational summary cards */}
      <OperationalSummaryCards
        summary={operationalSummary}
        activeKey={presetFilter}
        onSelect={handleSummaryCardSelect}
      />

      {/* Sticky Command Bar — pinned search / filters / create on mobile */}
      <StickyCommandBar>
        <CommandBarContent>
          <SearchContainer>
            <Search
              placeholder="Search order, customer, route..."
              allowClear
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onSearch={(value) => setSearchQuery(value)}
            />
          </SearchContainer>

          {!isMobile && (
            <ViewSegmented>
              <ViewButton
                $active={presetFilter === "active"}
                onClick={() => setPresetFilter("active")}
              >
                Active {tabCounts.active}
              </ViewButton>
              <ViewButton
                $active={presetFilter === "needs_attention"}
                onClick={() => setPresetFilter("needs_attention")}
                style={
                  tabCounts.needs_attention > 0
                    ? { color: "#ff4d4f", borderColor: "#ffa39e" }
                    : undefined
                }
              >
                <WarningOutlined /> Needs Attention {tabCounts.needs_attention}
              </ViewButton>
              <ViewButton
                $active={presetFilter === "upcoming"}
                onClick={() => setPresetFilter("upcoming")}
              >
                Upcoming {tabCounts.upcoming}
              </ViewButton>
              <ViewButton
                $active={presetFilter === "completed"}
                onClick={() => setPresetFilter("completed")}
              >
                Completed {tabCounts.completed}
              </ViewButton>
              <ViewButton
                $active={presetFilter === "cancelled"}
                onClick={() => setPresetFilter("cancelled")}
              >
                Cancelled {tabCounts.cancelled}
              </ViewButton>
            </ViewSegmented>
          )}

          <FilterButton
            icon={<FilterOutlined />}
            onClick={() => setFilterDrawerVisible(true)}
          >
            {isMobile ? null : "Filters"}
            {activeFiltersCount > 0 && (
              <FilterBadge count={activeFiltersCount} />
            )}
          </FilterButton>

          {!isMobile && (
            <Button
              icon={<ShareAltOutlined />}
              onClick={() => setPartnerJobPackModalVisible(true)}
              disabled={eligibleOrdersCount === 0}
            >
              Send to Partner
            </Button>
          )}

          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreateOrder}
          >
            {isMobile ? "New" : "Create Order"}
          </Button>
        </CommandBarContent>

        {isMobile && (
          <MobileTabRow>
            {[
              { key: "active", label: `Active (${tabCounts.active})` },
              {
                key: "needs_attention",
                label: `Attention (${tabCounts.needs_attention})`,
                danger: tabCounts.needs_attention > 0,
              },
              { key: "upcoming", label: `Upcoming (${tabCounts.upcoming})` },
              { key: "completed", label: `Completed (${tabCounts.completed})` },
              { key: "cancelled", label: `Cancelled (${tabCounts.cancelled})` },
            ].map((tab) => (
              <MobileTab
                key={tab.key}
                type="button"
                $active={presetFilter === tab.key}
                $danger={tab.danger}
                onClick={() => setPresetFilter(tab.key)}
              >
                {tab.label}
              </MobileTab>
            ))}
          </MobileTabRow>
        )}

        {/* Active Filters */}
        {getActiveFilters().length > 0 && (
          <ActiveFiltersContainer>
            {getActiveFilters().map((filter) => (
              <FilterChip
                key={filter.key}
                closable
                onClose={filter.onRemove}
              >
                {filter.label}
              </FilterChip>
            ))}
            <Button
              type="link"
              size="small"
              onClick={clearAllFilters}
              style={{ padding: 0 }}
            >
              Clear All
            </Button>
          </ActiveFiltersContainer>
        )}

        {/* Filter Precedence Warning */}
        {statusFilter !== "all" && (
          <ActiveFiltersContainer>
            <Alert
              message="Status filter is active and overrides view tabs"
              type="info"
              showIcon
              closable
              onClose={() => {
                setStatusFilter("all");
              }}
              style={{ marginTop: "8px", fontSize: "12px" }}
            />
          </ActiveFiltersContainer>
        )}
      </StickyCommandBar>

      {/* Content based on device type */}
      <MobileContentWrapper $hasBottomBar={isMobile && selectedOrder}>
      {loading ? (
        <div style={{ textAlign: "center", padding: "48px" }}>
          <Spin size="large" />
        </div>
      ) : isMobile ? (
        renderMobileView()
      ) : isTablet ? (
        renderTabletView()
      ) : (
        renderDesktopView()
      )}
      </MobileContentWrapper>

      {/* Order Modal */}
      <Modal
        title={
          selectedOrder
            ? isEditMode
              ? "Edit Order"
              : "View Order"
            : "Create New Order"
        }
        open={isModalVisible}
        onOk={isEditMode ? handleModalOk : undefined}
        onCancel={handleModalCancel}
        width={800}
        footer={
          isEditMode
            ? [
                <Button key="cancel" onClick={handleModalCancel}>
                  Cancel
                </Button>,
                <Button key="submit" type="primary" onClick={handleModalOk}>
                  {selectedOrder ? "Update" : "Create"}
                </Button>,
              ]
            : [
                <Button key="cancel" onClick={handleModalCancel}>
                  Close
                </Button>,
                <Button key="edit" type="primary" onClick={handleSwitchToEdit}>
                  Edit
                </Button>,
              ]
        }
      >
        <Form form={form} layout="vertical">
          {!isEditMode && selectedOrder && (
            <Row gutter={16} style={{ marginBottom: "16px" }}>
              <Col span={12}>
                <div
                  style={{
                    padding: "8px",
                    backgroundColor: "#f5f5f5",
                    borderRadius: "4px",
                  }}
                >
                  <strong>Order ID:</strong> {selectedOrder.id}
                </div>
              </Col>
              <Col span={12}>
                <div
                  style={{
                    padding: "8px",
                    backgroundColor: "#f5f5f5",
                    borderRadius: "4px",
                  }}
                >
                  <strong>Created:</strong>{" "}
                  {selectedOrder.createdAt
                    ? new Date(selectedOrder.createdAt).toLocaleDateString()
                    : "N/A"}
                </div>
              </Col>
            </Row>
          )}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="customer"
                label="Customer Name"
                rules={[
                  { required: true, message: "Please enter customer name" },
                ]}
              >
                <Input disabled={!isEditMode} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="customerPhone"
                label="Customer Phone"
                rules={[
                  { required: true, message: "Please enter customer phone" },
                ]}
              >
                <Input disabled={!isEditMode} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="pickup"
                label="Pickup Address"
                rules={[
                  { required: true, message: "Please enter pickup address" },
                ]}
              >
                <Input disabled={!isEditMode} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="delivery"
                label="Delivery Address"
                rules={[
                  { required: true, message: "Please enter delivery address" },
                ]}
              >
                <Input disabled={!isEditMode} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="status" label="Status">
                <Select disabled={!isEditMode}>
                  <Option value="pending">Pending</Option>
                  <Option value="booked">Booked</Option>
                  <Option value="in_progress">In Progress</Option>
                  <Option value="arrived_at_storage">Arrived at Storage</Option>
                  <Option value="in_transit_from_storage">In Transit from Storage</Option>
                  <Option value="completed">Completed</Option>
                  <Option value="cancelled">Cancelled</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="priority" label="Priority">
                <Select disabled={!isEditMode}>
                  <Option value="low">Low</Option>
                  <Option value="medium">Medium</Option>
                  <Option value="high">High</Option>
                  <Option value="urgent">Urgent</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="estimatedDelivery" label="Estimated Delivery">
                <DatePicker style={{ width: "100%" }} disabled={!isEditMode} />
              </Form.Item>
            </Col>
          </Row>
          {!isEditMode &&
            selectedOrder &&
            selectedOrder.items &&
            selectedOrder.items.length > 0 && (
              <Row gutter={16} style={{ marginTop: "16px" }}>
                <Col span={24}>
                  <Form.Item label="Items">
                    <div
                      style={{
                        padding: "12px",
                        backgroundColor: "#f5f5f5",
                        borderRadius: "4px",
                      }}
                    >
                      {selectedOrder.items.map((item, index) => {
                        // Handle both order items (description) and lead items (content/caption)
                        const itemDescription =
                          item.description ||
                          item.content ||
                          item.caption ||
                          `Item ${index + 1}`;
                        return (
                          <div
                            key={index}
                            style={{
                              marginBottom: "8px",
                              padding: "8px",
                              backgroundColor: "white",
                              borderRadius: "4px",
                            }}
                          >
                            <strong>{item.quantity}x</strong> {itemDescription}
                            {item.type && (
                              <span
                                style={{ color: "#666", marginLeft: "8px" }}
                              >
                                ({item.type})
                              </span>
                            )}
                            {item.weight && (
                              <span
                                style={{ color: "#666", marginLeft: "8px" }}
                              >
                                - {item.weight}kg
                              </span>
                            )}
                          </div>
                        );
                      })}
                      {selectedOrder.totalWeight && (
                        <div
                          style={{
                            marginTop: "8px",
                            padding: "8px",
                            backgroundColor: "#e6f7ff",
                            borderRadius: "4px",
                          }}
                        >
                          <strong>Total Weight:</strong>{" "}
                          {selectedOrder.totalWeight} kg
                        </div>
                      )}
                    </div>
                  </Form.Item>
                </Col>
              </Row>
            )}
        </Form>
      </Modal>

      {/* Feedback Request Modal */}
      <Modal
        title="Request Order Feedback"
        open={showFeedbackForm}
        onCancel={handleFeedbackCancel}
        footer={null}
        width={800}
        destroyOnHidden
      >
        {feedbackAlert && (
          <div
            style={{
              padding: "12px",
              borderRadius: "4px",
              marginBottom: "20px",
              background:
                feedbackAlert.type === "success" ? "#d4edda" : "#f8d7da",
              color: feedbackAlert.type === "success" ? "#155724" : "#721c24",
              border: `1px solid ${
                feedbackAlert.type === "success" ? "#c3e6cb" : "#f5c6cb"
              }`,
            }}
          >
            {feedbackAlert.message}
          </div>
        )}

        <FeedbackRequestForm
          onSubmit={handleFeedbackSubmit}
          onCancel={handleFeedbackCancel}
          loading={feedbackLoading}
          prePopulatedData={selectedOrder?.prePopulatedData}
          title="Request Order Feedback"
        />
      </Modal>

      {/* Filter Drawer */}
      <Drawer
        title="Filters"
        placement={isMobile ? "bottom" : "right"}
        width={isMobile ? "100%" : "400px"}
        height={isMobile ? "70vh" : "100%"}
        onClose={() => setFilterDrawerVisible(false)}
        open={filterDrawerVisible}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <div>
            <label style={{ display: "block", marginBottom: "8px", fontWeight: 600 }}>
              Status
            </label>
            <Select
              style={{ width: "100%" }}
              value={statusFilter}
              onChange={(value) => {
                setStatusFilter(value);
              }}
            >
              <Option value="all">All Status</Option>
              <Option value="pending">Pending</Option>
              <Option value="booked">Booked</Option>
              <Option value="in_progress">In Progress</Option>
              <Option value="arrived_at_storage">At Storage</Option>
              <Option value="in_transit_from_storage">In Transit from Storage</Option>
              <Option value="completed">Completed</Option>
              <Option value="cancelled">Cancelled</Option>
            </Select>
            {statusFilter !== "all" && (
              <div style={{ marginTop: "4px", fontSize: "11px", color: "#999" }}>
                This overrides view presets above
              </div>
            )}
          </div>

          <div>
            <label style={{ display: "block", marginBottom: "8px", fontWeight: 600 }}>
              Priority
            </label>
            <Select
              style={{ width: "100%" }}
              value={priorityFilter}
              onChange={(value) => setPriorityFilter(value)}
            >
              <Option value="all">All Priority</Option>
              <Option value="urgent">Urgent</Option>
              <Option value="normal">Normal</Option>
            </Select>
          </div>

          <div>
            <label style={{ display: "block", marginBottom: "8px", fontWeight: 600 }}>
              Trip allocation
            </label>
            <Select
              style={{ width: "100%" }}
              value={tripAssignmentFilter}
              onChange={(value) => setTripAssignmentFilter(value)}
            >
              <Option value="all">All orders</Option>
              <Option value="assigned">Assigned to trip</Option>
              <Option value="unassigned">Not on a trip</Option>
            </Select>
          </div>

          <div>
            <label style={{ display: "block", marginBottom: "8px", fontWeight: 600 }}>
              Group By
            </label>
            <Select
              style={{ width: "100%" }}
              value={groupBy}
              onChange={(value) => setGroupBy(value || "corridor_trip")}
            >
              <Option value="corridor_trip">Corridor & trip</Option>
              <Option value="corridor">Corridor only</Option>
              <Option value="customer">Customer</Option>
              <Option value="date">Date</Option>
            </Select>
          </div>

          <div>
            <label style={{ display: "block", marginBottom: "8px", fontWeight: 600 }}>
              Date Range
            </label>
            <DatePicker.RangePicker
              style={{ width: "100%" }}
              value={dateRangeFilter ? [dayjs(dateRangeFilter[0]), dayjs(dateRangeFilter[1])] : null}
              onChange={(dates) => {
                if (dates) {
                  setDateRangeFilter([dates[0].toDate(), dates[1].toDate()]);
                } else {
                  setDateRangeFilter(null);
                }
              }}
            />
          </div>

          <div style={{ marginTop: "16px", display: "flex", gap: "8px" }}>
            <Button
              block
              onClick={clearAllFilters}
            >
              Clear All
            </Button>
            <Button
              type="primary"
              block
              onClick={() => setFilterDrawerVisible(false)}
            >
              Apply Filters
            </Button>
          </div>
        </div>
      </Drawer>

      {/* Mobile Bottom Action Bar */}
      {isMobile && selectedOrder && (
        <MobileBottomBar>
          <Button
            type="primary"
            icon={<EyeOutlined />}
            onClick={() =>
              navigate(`/orders/${selectedOrder.id}`, {
                state: { order: selectedOrder },
              })
            }
            block
          >
            View Details
          </Button>
          {selectedOrder.customerPhone && (
            <>
              <Button
                icon={<PhoneOutlined />}
                href={`tel:${selectedOrder.customerPhone}`}
              />
              <Button
                icon={<MessageOutlined />}
                href={`https://wa.me/${formatPhoneForWhatsApp(selectedOrder.customerPhone)}`}
                target="_blank"
                disabled={!formatPhoneForWhatsApp(selectedOrder.customerPhone)}
                style={{ color: "#25d366", borderColor: "#25d366" }}
              />
            </>
          )}
        </MobileBottomBar>
      )}

      {/* Map Drawer */}
      <Drawer
        title={
          <div>
            <div
              style={{ fontSize: "16px", fontWeight: 600, marginBottom: "4px" }}
            >
              Route Map
            </div>
            {mapRoute && (
              <div style={{ fontSize: "12px", color: "#666" }}>
                {mapRoute.pickup} → {mapRoute.delivery}
              </div>
            )}
          </div>
        }
        placement={isMobile ? "bottom" : "right"}
        width={isMobile ? "100%" : mapDrawerExpanded ? "75%" : "600px"}
        height={isMobile ? "80vh" : "100%"}
        onClose={() => {
          setMapDrawerVisible(false);
          setMapRoute(null);
          setMapDrawerExpanded(false);
        }}
        open={mapDrawerVisible}
        extra={
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            {!isMobile && (
              <Button
                type="text"
                icon={
                  mapDrawerExpanded ? <CompressOutlined /> : <ExpandOutlined />
                }
                onClick={() => setMapDrawerExpanded(!mapDrawerExpanded)}
                title={mapDrawerExpanded ? "Compress map" : "Expand map"}
              />
            )}
            <Button
              type="text"
              icon={<CloseOutlined />}
              onClick={() => {
                setMapDrawerVisible(false);
                setMapRoute(null);
                setMapDrawerExpanded(false);
              }}
            />
          </div>
        }
      >
        {mapRoute && (
          <div
            style={{
              height: isMobile ? "calc(80vh - 80px)" : "calc(100vh - 120px)",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                flex: 1,
                minHeight: 0,
                borderRadius: "8px",
                overflow: "hidden",
                border: "1px solid #e8e8e8",
                backgroundColor: "#f5f5f5",
              }}
            >
              <RouteMapWrapper
                pickup={mapRoute.pickup}
                delivery={mapRoute.delivery}
                drawerExpanded={mapDrawerExpanded}
                pickupAddress={mapRoute.pickupAddress}
                deliveryAddress={mapRoute.deliveryAddress}
              />
            </div>
            <div style={{ marginTop: "16px", display: "flex", gap: "8px" }}>
              <Button
                type="primary"
                icon={<EnvironmentOutlined />}
                href={getGoogleMapsUrl(mapRoute.pickup, mapRoute.delivery)}
                target="_blank"
                block={isMobile}
              >
                Open Route in Google Maps
              </Button>
            </div>
          </div>
        )}
      </Drawer>

      {/* Partner Job Pack Modal */}
      <PartnerJobPackModal
        visible={partnerJobPackModalVisible}
        onClose={() => setPartnerJobPackModalVisible(false)}
        orders={orders}
      />
    </PageContainer>
  );
};

export default OrdersPage;
